/**
 * Auth API. Mirrors the endpoints documented in docs/API-CONTRACT.md.
 *
 * Stateless on its own; the `authStore` in `@/hooks/useAuth` wraps these
 * with token storage and a tiny reactive user cache.
 */

import { api } from "./client";

export type UserRole = "admin" | "tutor" | "student" | "parent";

export interface AuthUser {
  id: string | number;
  /** Children created by a parent sign in with a username and may have no email. */
  email: string | null;
  username?: string | null;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  role?: UserRole;
  avatar?: string | null;
  /** A parent who opened an onboarding link but has not added a child yet. */
  onboarding_pending?: boolean;
  is_staff?: boolean;
  /** Wire field from `auth/me/`. Read `isOwner` in the app. */
  is_owner?: boolean;
  /** The institute owner: sees Subscription and can reach billing recovery. */
  isOwner: boolean;
  /** The account owner. Only superusers see Settings > Connections. */
  is_superuser?: boolean;
  /** Our LoopDesk team: answers help queries and moves their status. */
  is_loopdesk_team?: boolean;
}

export interface TokenPair {
  access: string;
  refresh: string;
  user: AuthUser;
}

/** What an invite token resolves to, for greeting someone on /activate. */
export interface InviteCheck {
  email: string;
  first_name: string;
  role: UserRole;
  expires_at: string;
}

export interface SignupPayload {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  /** Invite token from the URL. DrTutor is invite-only. */
  token?: string;
}

/** Adds the camelCase `isOwner` the app reads from the wire `is_owner`. */
function normaliseUser(user: AuthUser): AuthUser {
  return { ...user, isOwner: user.is_owner === true };
}

function withOwner(pair: TokenPair): TokenPair {
  return { ...pair, user: normaliseUser(pair.user) };
}

export const authApi = {
  /** `identifier` is an email address or a child's username. */
  login(identifier: string, password: string): Promise<TokenPair> {
    return api
      .post<TokenPair>("/auth/login/", { identifier, password })
      .then((r) => withOwner(r.data));
  },

  signup(payload: SignupPayload): Promise<TokenPair> {
    return api
      .post<TokenPair>("/auth/signup/", payload)
      .then((r) => withOwner(r.data));
  },

  logout(refresh: string | null): Promise<void> {
    return api
      .post<void>("/auth/logout/", refresh ? { refresh } : undefined)
      .then(() => undefined);
  },

  signOutAll(): Promise<void> {
    return api.post<void>("/auth/sign-out-all/", {}).then(() => undefined);
  },

  me(): Promise<AuthUser> {
    return api.get<AuthUser>("/auth/me/").then((r) => normaliseUser(r.data));
  },

  forgotPassword(email: string): Promise<void> {
    return api
      .post<void>("/auth/forgot-password/", { email })
      .then(() => undefined);
  },

  resetPassword(payload: {
    uid: string;
    token: string;
    new_password: string;
  }): Promise<void> {
    return api
      .post<void>("/auth/reset-password/", payload)
      .then(() => undefined);
  },

  /** Email a one-time sign-in link. Always resolves the same way, whether
   *  or not an account exists (no enumeration). */
  requestMagicLink(email: string): Promise<void> {
    return api
      .post<void>("/auth/magic-link/", { email })
      .then(() => undefined);
  },

  /** Spend a sign-in link. Returns the same token pair as login. Throws
   *  ApiError(400) with code magic_link_expired, magic_link_used or
   *  magic_link_invalid. */
  verifyMagicLink(token: string): Promise<TokenPair> {
    return api
      .post<TokenPair>("/auth/magic-link/verify/", { token })
      .then((r) => withOwner(r.data));
  },

  /**
   * Is this magic link still good, and who is it for? Called on /activate
   * mount so the page can greet them by name instead of showing a bare
   * password box. Throws ApiError(400) for expired, revoked and unknown
   * tokens alike (the backend does not distinguish them).
   */
  verifyInvite(token: string): Promise<InviteCheck> {
    return api
      .post<InviteCheck>("/auth/invite/verify/", { token })
      .then((r) => r.data);
  },

  /** Claim the account. Returns a token pair, so they land signed in. */
  acceptInvite(payload: { token: string; password: string }): Promise<TokenPair> {
    return api
      .post<TokenPair>("/auth/invite/accept/", payload)
      .then((r) => withOwner(r.data));
  },

  changePassword(payload: {
    current_password: string;
    new_password: string;
  }): Promise<void> {
    return api
      .post<void>("/auth/change-password/", payload)
      .then(() => undefined);
  },

  /**
   * PATCH /auth/me/. Accepts either a JSON payload (first_name / last_name /
   * phone) or a FormData (when updating the avatar image). The backend's
   * `UserMeSerializer` keeps email + role read-only.
   */
  updateMe(
    payload: Partial<Pick<AuthUser, "first_name" | "last_name">> & {
      phone?: string;
    },
  ): Promise<AuthUser> {
    return api.patch<AuthUser>("/auth/me/", payload).then((r) => normaliseUser(r.data));
  },

  updateMeAvatar(file: File): Promise<AuthUser> {
    const fd = new FormData();
    fd.append("avatar", file);
    return api.patch<AuthUser>("/auth/me/", fd).then((r) => normaliseUser(r.data));
  },
};
